/**
 * The line that says a form is checked, and the thing that makes the check visible.
 *
 * **reCAPTCHA v3 has no widget.** There is no puzzle, no "I am not a robot" box, nothing to tick —
 * that is the whole point of v3, and it is why a correctly working installation looks exactly like
 * one that was never configured. The only visible sign is Google's floating badge, and the only
 * proof for a reader is a sentence saying so. This renders both: it primes the script as the form
 * mounts, which is what brings the badge up, and it prints the attribution Google's terms require
 * anybody using v3 to show.
 *
 * It draws nothing at all when no keys are configured, so a development machine and a self-hosted
 * installation without a Google account are not left claiming a protection they do not have.
 */

import { useEffect, useState } from "react";
import { primeRecaptcha, recaptchaConfigured } from "../lib/recaptcha";

export function RecaptchaNotice({ className }: { className?: string }) {
  const [on, setOn] = useState(false);

  useEffect(() => {
    let alive = true;
    /*
      Primed here rather than on the first press. Fetching Google's script takes a moment, and
      doing it while somebody is still filling the form means the badge is up before they finish
      and the submit does not wait on a round trip it could have made earlier.
    */
    primeRecaptcha();
    void recaptchaConfigured().then((configured) => {
      if (alive) setOn(configured);
    });
    return () => {
      alive = false;
    };
  }, []);

  if (!on) return null;

  return (
    <p className={`text-xs leading-relaxed text-ink-400 ${className ?? "mt-4"}`}>
      Protected by reCAPTCHA. Google's{" "}
      <a
        className="underline underline-offset-2 hover:text-ink-600"
        href="https://policies.google.com/privacy"
        target="_blank"
        rel="noreferrer noopener"
      >
        privacy policy
      </a>{" "}
      and{" "}
      <a
        className="underline underline-offset-2 hover:text-ink-600"
        href="https://policies.google.com/terms"
        target="_blank"
        rel="noreferrer noopener"
      >
        terms
      </a>{" "}
      apply. There is nothing for you to solve — it scores the request in the background and only
      shows itself if something looks wrong.
    </p>
  );
}
